// src/services/tables/teamStatsColumns.js - TEAM STATS COLUMNS WITH POSITION SLOTS AND 3-LINE FORMAT
import React from 'react';
import { ExternalLink, Zap, Activity, Move, Trash2, ArrowLeftRight } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { formatContractDisplay } from '../contractUtils';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

// Helper for 3-row border styling
const get3RowBorderClass = (player, rowType, item) => {
  if (rowType === 'accrued') {
    return `bg-neutral-800 border-b border-yellow-500/20`;
  }
  return 'bg-neutral-800';
};

// Helper for font sizing and style on rows 2 and 3 (rolling and accrued)
const getStatRowFontClass = (row) => {
  // Row 1 (season) = normal size
  if (row.statType === 'season') return '';
  
  // Rows 2 & 3 (rolling, accrued) = smaller and italic
  return 'text-xs italic';
};



/**
 * Create columns for team stats active lineup with 3-line format per position slot
 * Uses position slot structure like MyRoster
 */
export const createTeamStatsActiveLineupColumns = ({
  statConfigs = [],
  onPlayerClick,
  showHistoricalBadge = false,
  isPitcher = false,
  onMovePlayer,
  onDropPlayer,
  onTradePlayer,
  hasBenchSlots = false,
  leagueSettings = {}
}) => {
  const columns = [
    // Position Label
    {
      key: 'slotPosition',
      title: 'POS',
      width: 28,  // Minimal width for POS
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: get3RowBorderClass,  // Consistent border styling
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        // Show position on middle row (rolling) for vertical centering
        if (rowType === 'rolling') {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.primary} text-sm uppercase tracking-wide`}>
                {slotInfo.slotPosition || '?'}
              </span>
            </div>
          );
        }
        return null;
      }
    },
    
    // Player Name with stat type indicator
    {
      key: 'player_name',
      title: 'Player',
      width: 90,  // Increased by 50% from 60px
      cellPadding: 'py-0 pl-1 pr-0',  // Remove right padding
      textAlign: 'text-left',  // Explicit left alignment
      sortable: false,
      allowOverflow: true,
      className: get3RowBorderClass,  // Consistent border styling
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        // Season row - show player or "Empty"
        if (rowType === 'season') {
          if (!player) {
            return (
              <span className={`${dynastyTheme.classes.text.neutralDark} text-sm italic`}>
                Empty
              </span>
            );
          }
          
          return (
            <div className="inline-flex items-center gap-2 w-full">
              <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
              <InjuryBadge mlbId={player.ids?.mlb} />
              {player.trend === 'hot' && <Zap className="w-3 h-3 text-red-400" />}
              {player.trend === 'cold' && <Activity className="w-3 h-3 text-blue-400" />}
              {showHistoricalBadge && player.is_historical && (
                <span className="px-2 py-0.5 rounded text-xs bg-neutral-700 text-neutral-300">
                  Alumni
                </span>
              )}
            </div>
          );
        } else {
          // 14-Day and Accrued rows - show stat type label
          return (
            <div className={`text-left text-[10px] font-normal opacity-50 ${
              rowType === 'rolling' 
                ? 'text-yellow-400'
                : 'text-cyan-400'
            }`}>
              {rowType === 'rolling' ? '↗ 14-Day' : '✓ Accrued'}
            </div>
          );
        }
      }
    },
    
    // Team
    {
      key: 'team',
      title: 'Team',
      width: 32,  // Narrower width for Team
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, item) => {
        let classes = ['bg-neutral-800'];
        if (rowType === 'accrued') {
          classes.push('border-b border-yellow-500/20');
        }
        return classes.join(' ');
      },
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        // Show team on middle row (rolling) for vertical centering
        if (rowType === 'rolling' && player) {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.white} text-sm`}>
                {player.info?.mlb_team || '-'}
              </span>
            </div>
          );
        }
        return null;
      }
    }
  ];

  // Add static stat columns (AB, H for batters OR GS for pitchers).
  // Phase 3 cleanup: dropped static G for pitchers — G is injected via the
  // statConfigs prepend in TeamStats.js (line 104: pitchingPrepend includes
  // 'G'), so a static G column would duplicate the dynamic one.
  if (isPitcher) {
    columns.push({
      key: 'games_started',
      title: 'GS',
      width: 28,  // Narrower for 2 letters
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      className: get3RowBorderClass,
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        if (!player) return null;
        
        // CANONICAL PATTERN - match My Roster exactly
        let statsData;
        if (rowType === 'season') statsData = player.stats?.season?.pitching;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day?.pitching;
        else if (rowType === 'accrued') statsData = player.stats?.active_accrued?.pitching;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        const value = statsData.games_started;
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : rowType === 'rolling' ? 'text-yellow-500/70' : 'text-cyan-500/60';
        const fontClass = getStatRowFontClass({statType: rowType});
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
      }
    });
  }
  // DD-STAT-CONSOLIDATION P0: removed duplicate static batter G/AB/H block (supplied via
  // the statConfigs prepend) + its ACCRUED DEBUG console.log. Dynamic columns render G/AB/H.

  // Add dynamic stat columns from league settings
  statConfigs.forEach((config) => {
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: 32,  // Narrower for dynamic stats
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      className: (player, rowType, item) => {
        let classes = ['bg-neutral-800'];
        if (rowType === 'accrued') {
          classes.push('border-b border-yellow-500/20');
        }
        return classes.join(' ');
      },
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        if (!player) return null;
        
        // CANONICAL PATTERN - match My Roster exactly
        let statsData;
        if (rowType === 'season') statsData = player.stats?.season;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
        else if (rowType === 'accrued') statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // Handle nested path like "pitching.era" or "hitting.avg"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        // FORMAT BASED ON CONFIG decimals AND prefix (not 'format' property)
        let formattedValue;
        if (config.decimals === 3 && config.prefix === '.') {
          formattedValue = renderAvg(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else if (config.decimals === 2) {
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          formattedValue = renderFloat1(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : rowType === 'rolling' ? 'text-yellow-500/70' : 'text-cyan-500/60';
        const fontClass = getStatRowFontClass({statType: rowType});
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // PRICE column (from salary engine)
  columns.push({
    key: 'price',
    title: 'Price',
    width: 38,  // Narrower for Price
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      let classes = ['bg-neutral-800'];
      if (rowType === 'accrued') {
        classes.push('border-b border-yellow-500/20');
      }
      return classes.join(' ');
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      // Show price on middle row (rolling) for vertical centering
      if (rowType !== 'rolling' || !player) return null;

      const price = player.league_context?.financial?.baseline_price;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
            ${price}
          </span>
        </div>
      );
    }
  });

  // Salary column
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 42,  // Narrower for Salary
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      let classes = ['bg-neutral-800'];
      if (rowType === 'accrued') {
        classes.push('border-b border-yellow-500/20');
      }
      return classes.join(' ');
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      // Show salary on middle row (rolling) for vertical centering
      if (rowType !== 'rolling' || !player) return null;

      const salary = player.league_context?.financial?.contract_salary;
      
      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
            ${salary}
          </span>
        </div>
      );
    }
  });

  // Contract column
  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 52,  // Narrower for Contract
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      let classes = ['bg-neutral-800'];
      if (rowType === 'accrued') {
        classes.push('border-b border-yellow-500/20');
      }
      return classes.join(' ');
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      // Show contract on middle row (rolling) for vertical centering
      if (rowType !== 'rolling' || !player) return null;

      const display = formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
            {display}
          </span>
        </div>
      );
    }
  });

  return columns;
};

/**
 * Create columns for non-lineup players (bench, historical, etc.)
 * This is a simpler format without position slots
 */
export const createTeamStatsReserveColumns = ({
  statConfigs = [],
  onPlayerClick,
  showHistoricalBadge = false,
  isPitcher = false,
  onMovePlayer,
  onDropPlayer,
  onTradePlayer,
  hasBenchSlots = false,
  leagueSettings = {}
}) => {
  const columns = [
    // POS column FIRST!
    {
      key: 'position',
      title: 'POS',
      width: 28,  // Narrower POS
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType, item) => {
        let classes = ['bg-neutral-800'];
        if (rowType === 'accrued') {
          classes.push('border-b border-yellow-500/20');
        }
        return classes.join(' ');
      },
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        if (rowType === 'rolling' && player) {
          return (
            <span className={`${dynastyTheme.classes.text.primary} text-xs font-bold uppercase tracking-wide`}>
              {player.info?.position || '?'}
            </span>
          );
        }
        return null;
      }
    },
    // Player Name
    {
      key: 'player_name',
      title: 'Player',
      width: 90,  // Increased by 50% from 60px
      cellPadding: 'py-0 pl-1 pr-0',  // Remove right padding
      textAlign: 'text-left',  // Explicit left alignment
      sortable: false,
      allowOverflow: true,
      className: (player, rowType, item) => {
        let classes = ['bg-neutral-800'];
        if (rowType === 'accrued') {
          classes.push('border-b border-yellow-500/20');
        }
        return classes.join(' ');
      },
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        if (rowType === 'season') {
        if (!player) return null;
          return (
            <div className="inline-flex items-center gap-2 w-full">
              <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
              <InjuryBadge mlbId={player.ids?.mlb} />
              {showHistoricalBadge && player.is_historical && (
                <span className="px-2 py-0.5 rounded text-xs bg-neutral-700 text-neutral-300">
                  Alumni
                </span>
              )}
            </div>
          );
        } else {
          // Match active lineup stat type indicators: smaller text, lower opacity
          return (
            <div className={`text-left text-[10px] font-normal opacity-50 ${
              rowType === 'rolling' 
                ? 'text-yellow-400'
                : 'text-cyan-400'
            }`}>
              {rowType === 'rolling' ? '↗ 14-Day' : '✓ Accrued'}
            </div>
          );
        }
      }
    }
  ];

  // Add Team column
  columns.push({
    key: 'team',
    title: 'Team',
    width: 32,  // Narrower Team
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      if (rowType === 'accrued') {
        return `bg-neutral-800 border-b border-yellow-500/20`;
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      if (rowType === 'season' && player) {
        return (
          <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
            {player.info?.mlb_team || '-'}
          </span>
        );
      }
      return null;
    }
  });

  // Add static stat columns based on player type.
  // Phase 3 cleanup: dropped games_played from the static list — G is
  // injected via statConfigs prepend ('G' in pitchingPrepend at
  // TeamStats.js:104), so the static entry would duplicate it.
  if (isPitcher) {
    // Pitchers: GS only (G arrives via dynamic statConfigs)
    const staticPitcherStats = [
      { field: 'games_started', label: 'GS', width: 28 }
    ];
    
    staticPitcherStats.forEach(stat => {
      columns.push({
        key: stat.field,
        title: stat.label,
        width: stat.width,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        className: (player, rowType, item) => {
          if (rowType === 'accrued') {
            return `bg-neutral-800 border-b border-yellow-500/20`;
          }
          return 'bg-neutral-800';
        },
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          // CANONICAL PATTERN - match My Roster exactly
          let statsData;
          if (rowType === 'season') statsData = player.stats?.season?.pitching;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day?.pitching;
          else if (rowType === 'accrued') statsData = player.stats?.active_accrued?.pitching;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          const value = statsData[stat.field];
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : rowType === 'rolling' ? 'text-yellow-500/70' : 'text-cyan-500/60';
          const fontClass = getStatRowFontClass({statType: rowType});
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
        }
      });
    });
  }
  // DD-STAT-CONSOLIDATION P0: removed duplicate static batter G/AB/H block (supplied via
  // the statConfigs prepend). Dynamic columns below render G/AB/H.

  // Add dynamic stat columns from league settings
  statConfigs.forEach((config) => {
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: 32,  // Narrower for dynamic stats
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      className: (player, rowType, item) => {
        if (rowType === 'accrued') {
          return `bg-neutral-800 border-b border-yellow-500/20`;
        }
        return 'bg-neutral-800';
      },
      render: (player, rowType, slotInfo) => {
          if (!player) return null;
        // CANONICAL PATTERN - match My Roster exactly
        let statsData;
        if (rowType === 'season') statsData = player.stats?.season;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
        else if (rowType === 'accrued') statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // Handle nested path like "pitching.era" or "hitting.avg"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        // FORMAT BASED ON CONFIG decimals AND prefix (not 'format' property)
        let formattedValue;
        if (config.decimals === 3 && config.prefix === '.') {
          formattedValue = renderAvg(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else if (config.decimals === 2) {
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          formattedValue = renderFloat1(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : rowType === 'rolling' ? 'text-yellow-500/70' : 'text-cyan-500/60';
        const fontClass = getStatRowFontClass({statType: rowType});
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // PRICE column - Show on ROLLING row (2nd row)
  columns.push({
    key: 'price',
    title: 'Price',
    width: 38,  // Narrower
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      if (rowType === 'accrued') {
        return `bg-neutral-800 border-b border-yellow-500/20`;
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      if (rowType !== 'rolling') return null;

      const price = player.league_context?.financial?.baseline_price;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
            ${price}
          </span>
        </div>
      );
    }
  });

  // Salary column - Show on ROLLING row (2nd row)
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 42,  // Narrower
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      if (rowType === 'accrued') {
        return `bg-neutral-800 border-b border-yellow-500/20`;
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      if (rowType !== 'rolling') return null;

      const salary = player.league_context?.financial?.contract_salary;
      
      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
            ${salary}
          </span>
        </div>
      );
    }
  });

  // Contract column - Show on ROLLING row (2nd row)
  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 52,  // Narrower
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      if (rowType === 'accrued') {
        return `bg-neutral-800 border-b border-yellow-500/20`;
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      if (rowType !== 'rolling') return null;

      const display = formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
            {display}
          </span>
        </div>
      );
    }
  });

  // Actions column - Show on ROLLING row (2nd row)
  columns.push({
    key: 'actions',
    title: 'Actions',
    width: 48,  // Narrower
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType, item) => {
      if (rowType === 'accrued') {
        return `bg-neutral-800 border-b border-yellow-500/20`;
      }
      return 'bg-neutral-800';
    },
    render: (player, rowType, slotInfo) => {
          if (!player) return null;
      if (rowType !== 'rolling') return null;
      
      const canMove = hasBenchSlots || 
                     (leagueSettings.enableMinors && player.minors_eligible) || 
                     (leagueSettings.enableDL && player.dl_eligible);
      
      return (
        <div className="flex items-center justify-center h-full gap-1">
          <button
            onClick={() => onMovePlayer && onMovePlayer(player)}
            disabled={!canMove}
            className={`p-1 text-xs rounded transition-all ${
              canMove 
                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 cursor-pointer'
                : 'bg-gray-700/20 text-gray-600 cursor-not-allowed opacity-50'
            }`}
            title={!canMove ? 'No bench slots or special positions available' : 'Move player'}
          >
            <Move className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDropPlayer && onDropPlayer(player)}
            className="p-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
            title="Drop player"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <button
            onClick={() => onTradePlayer && onTradePlayer(player)}
            className="p-1 text-xs rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-all"
            title="Trade player"
          >
            <ArrowLeftRight className="w-3 h-3" />
          </button>
        </div>
      );
    }
  });

  return columns;
};







// ========================================
// ACCRUED ONLY MODE - SINGLE ROW FORMAT
// ========================================

/**
 * Create columns for ACCRUED ONLY mode - active lineup
 * Single row per player with ONLY accrued stats, normal styling (not italic/smaller)
 */
export const createTeamStatsActiveLineupColumnsAccruedOnly = ({
  statConfigs = [],
  onPlayerClick,
  showHistoricalBadge = false,
  isPitcher = false,
  onMovePlayer,
  onDropPlayer,
  onTradePlayer,
  hasBenchSlots = false,
  leagueSettings = {},
  isTeamTotalsTable = false
}) => {
  const columns = [
    // Position Label
    {
      key: 'slotPosition',
      title: 'POS',
      width: 28,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: (player, rowType, item) => {
        if (item.isTotalsRow) return null;

        return (
          <div className="flex items-center justify-center h-full">
            <span className={`${dynastyTheme.classes.text.primary} text-sm uppercase tracking-wide`}>
              {item.slotPosition || '?'}
            </span>
          </div>
        );
      }
    },
    
    // Player Name
    {
      key: 'player_name',
      title: 'Player',
      width: 90,  // Increased by 50% from 60px
      sortable: false,
      className: () => 'bg-neutral-800',
      allowOverflow: true,
      render: (player, rowType, item) => {
        if (item.isTotalsRow) {
          return (
            <span className={`${dynastyTheme.classes.text.white} text-sm font-bold`}>
              Team Totals
            </span>
          );
        }

        if (!player) {
          return (
            <span className={`${dynastyTheme.classes.text.neutralDark} text-sm italic`}>
              Empty
            </span>
          );
        }
        
        return (
          <div className="inline-flex items-center gap-2 w-full">
            <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
            <InjuryBadge mlbId={player.ids?.mlb} />
            {player.trend === 'hot' && <Zap className="w-3 h-3 text-red-400" />}
            {player.trend === 'cold' && <Activity className="w-3 h-3 text-blue-400" />}
            {showHistoricalBadge && player.is_historical && (
              <span className="px-2 py-0.5 rounded text-xs bg-neutral-700 text-neutral-300">
                Alumni
              </span>
            )}
          </div>
        );
      }
    },

    // Team
    {
      key: 'team',
      title: 'Team',
      width: 32,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: (player, rowType, item) => {
        if (!player) return null;
        
        if (player) {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.white} text-sm`}>
                {player.info?.mlb_team || '-'}
              </span>
            </div>
          );
        }
        return null;
      }
    }
  ];

  // Add static stat columns.
  // Phase 3 cleanup: dropped static G for pitchers (duplicate of dynamic G
  // injected via statConfigs prepend in TeamStats.js).
  if (isPitcher) {
    columns.push({
      key: 'games_started',
      title: 'GS',
      width: 28,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      isStatColumn: true,
      render: (player, rowType, item) => {
        if (!player) {
          // DD-STAT-CONSOLIDATION P0: no `|| 0` fabrication — absent totals render '-'
          const value = item.totals?.games_started;
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{renderDefault(value)}</span>;
        }

        // CANONICAL PATTERN - match My Roster exactly (accrued only)
        const statsData = player.stats?.active_accrued?.pitching;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        const value = statsData.games_started;
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        return <span className={`text-cyan-500/60 font-mono text-sm`}>{renderDefault(value)}</span>;
      }
    });
  }
  // DD-STAT-CONSOLIDATION P0: batter G/AB/H are supplied via the statConfigs prepend
  // (TeamStats.js hittingPrepend). The former static G/AB/H block duplicated them AND
  // carried a `item.totals?.[field] || 0` fallback that fabricated the 0/0/0 totals row
  // (Rule 9 violation). Removed — the dynamic columns below render + total G/AB/H.

  // Add dynamic stat columns
  statConfigs.forEach((config) => {
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: 32,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      isStatColumn: true,
      render: (valueOrPlayer, flatDataOrRowType, itemOrIndex) => {
        // Handle flat-data signature (for Team Totals table)
        if (isTeamTotalsTable && (typeof valueOrPlayer === 'number' || valueOrPlayer === null || valueOrPlayer === undefined)) {
          // Extract simple field name from config.field (e.g., "runs" from "hitting.runs")
          let fieldName = config.field;
          if (fieldName.includes('.')) {
            fieldName = fieldName.split('.').pop();
          }
          const value = valueOrPlayer ?? flatDataOrRowType?.[fieldName] ?? null;
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          let formattedValue;
          if (config.decimals === 3 && config.prefix === '.') {
            formattedValue = renderAvg(value);
          } else if (config.decimals === 3) {
            formattedValue = renderFloat3(value);
          } else if (config.decimals === 2) {
            formattedValue = renderFloat2(value);
          } else if (config.decimals === 1) {
            formattedValue = renderFloat1(value);
          } else {
            formattedValue = renderDefault(value);
          }
          return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{formattedValue}</span>;
        }
        // Handle regular player-data signature
        const player = valueOrPlayer;
        const rowType = flatDataOrRowType;
        const item = itemOrIndex;
        if (!player) {
          // Extract the actual field name (remove "hitting." or "pitching." prefix if present)
          let fieldName = config.field;
          if (fieldName.includes('.')) {
            const parts = fieldName.split('.');
            fieldName = parts[parts.length - 1];  // Get last part (e.g., "runs" from "hitting.runs")
          }
          
          const value = isTeamTotalsTable ? item.flatData?.[fieldName] : item.totals?.[fieldName];

          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          let formattedValue;
          if (config.decimals === 3 && config.prefix === '.') {
            formattedValue = renderAvg(value);
          } else if (config.decimals === 3) {
            formattedValue = renderFloat3(value);
          } else if (config.decimals === 2) {
            formattedValue = renderFloat2(value);
          } else if (config.decimals === 1) {
            formattedValue = renderFloat1(value);
          } else {
            formattedValue = renderDefault(value);
          }
          
          return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{formattedValue}</span>;
        }
        
        if (!player) return null;
        
        // CANONICAL PATTERN - match My Roster exactly (accrued only)
        const statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // Handle nested path like "pitching.era" or "hitting.avg"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        let formattedValue;
        if (config.decimals === 3 && config.prefix === '.') {
          formattedValue = renderAvg(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else if (config.decimals === 2) {
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          formattedValue = renderFloat1(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        return <span className={`text-cyan-500/60 font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // PRICE, SALARY, CONTRACT, ACTIONS - all show on this row
  columns.push({
    key: 'price',
    title: 'Price',
    width: 38,  // Narrower
    cellPadding: 'py-1 px-1',
    sortable: false,
      className: () => 'bg-neutral-800',
    render: (player, rowType, item) => {
      if (!player) return null;
      if (!player) return null;

      const price = player.league_context?.financial?.baseline_price;
      return (
        <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
          ${price}
        </span>
      );
    }
  });

  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 42,  // Narrower
    cellPadding: 'py-1 px-1',
    sortable: false,
      className: () => 'bg-neutral-800',
    render: (player, rowType, item) => {
      if (!player) return null;
      if (!player) return null;

      const salary = player.league_context?.financial?.contract_salary;
      return (
        <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
          ${salary}
        </span>
      );
    }
  });

  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 52,  // Narrower
    cellPadding: 'py-1 px-1',
    sortable: false,
      className: () => 'bg-neutral-800',
    render: (player, rowType, item) => {
      if (!player) return null;
      if (!player) return null;

      const display = formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);

      return (
        <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
          {display}
        </span>
      );
    }
  });

  return columns;
};

/**
 * Create columns for ACCRUED ONLY mode - reserve/historical players
 * Single row per player with ONLY accrued stats
 */
export const createTeamStatsReserveColumnsAccruedOnly = ({
  statConfigs = [],
  onPlayerClick,
  showHistoricalBadge = false,
  isPitcher = false,
  isTeamTotalsTable = false
}) => {
  const columns = [];
  
  // For Team Totals table: single label column instead of POS + Player + Team
  if (isTeamTotalsTable) {
    columns.push({
      key: 'label',
      title: '',
      width: 75,  // Reduced by 50%
      cellPadding: 'py-1 px-2',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: () => (
        <span className={`${dynastyTheme.classes.text.primary} font-bold text-sm`}>
          TEAM TOTAL
        </span>
      )
    });
  } else {
    // For regular player tables: POS + Player + Team columns
    columns.push(
      // POS column
      {
        key: 'position',
        title: 'POS',
        width: 28,  // Narrower
        cellPadding: 'py-1 px-1',
        sortable: false,
      className: () => 'bg-neutral-800',
        render: (player, rowType, item) => {
        if (item.isTotalsRow) {
        return (
        <span className={`${dynastyTheme.classes.text.primary} text-sm font-bold uppercase`}>
        TOTAL
        </span>
        );
        }

        if (!player) return null;

        return (
        <span className={`${dynastyTheme.classes.text.primary} text-sm font-bold uppercase tracking-wide`}>
            {player.info?.position || '?'}
            </span>
          );
        }
      },
      
      // Player Name
      {
        key: 'player_name',
        title: 'Player',
        width: 90,  // Increased by 50% from 60px
        sortable: false,
      className: () => 'bg-neutral-800',
        allowOverflow: true,
        render: (player, rowType, item) => {
          if (item.isTotalsRow) {
            return (
              <span className={`${dynastyTheme.classes.text.white} text-sm font-bold text-right`}>
                Team Totals
              </span>
            );
          }

          if (!player) return null;
          
          return (
            <div className="inline-flex items-center gap-2 w-full">
              <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
              {showHistoricalBadge && player.info?.is_historical && (
                <span className="ml-2 px-2 py-0.5 rounded text-xs bg-neutral-700 text-neutral-300">
                  Alumni
                </span>
              )}
            </div>
          );
        }
      },
      
      // Team column
      {
        key: 'team',
        title: 'Team',
        width: 32,  // Narrower
        cellPadding: 'py-1 px-1',
        sortable: false,
      className: () => 'bg-neutral-800',
        render: (player, rowType, item) => {
          if (!player) return null;
          
          return (
            <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
              {player.info?.mlb_team || '-'}
            </span>
          );
        }
      }
    );
  }

  // Add static stat columns.
  // Phase 3 cleanup: dropped games_played from the static list (duplicate of
  // dynamic G injected via statConfigs prepend).
  if (isPitcher) {
    const staticPitcherStats = [
      { field: 'games_started', label: 'GS', width: 28 }
    ];
    
    staticPitcherStats.forEach(stat => {
      columns.push({
        key: stat.field,
        title: stat.label,
        width: stat.width,
        cellPadding: 'py-1 px-1',
        sortable: false,
      className: () => 'bg-neutral-800',
        isStatColumn: true,
        render: (valueOrPlayer, flatDataOrRowType, itemOrIndex) => {
          // Handle flat-data signature (for Team Totals table)
          if (typeof valueOrPlayer === 'number' || valueOrPlayer === null || valueOrPlayer === undefined) {
            // DD-STAT-CONSOLIDATION P0: no `?? 0` fabrication — absent renders '-'
            const value = valueOrPlayer ?? flatDataOrRowType?.[stat.field] ?? null;
            if (value === undefined || value === null) {
              return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
            }
            return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{renderDefault(value)}</span>;
          }

          // Handle player-data signature
          const player = valueOrPlayer;
          const item = itemOrIndex;

          if (!player) {
            // DD-STAT-CONSOLIDATION P0: no `|| 0` fabrication — absent renders '-'
            const value = item.totals?.[stat.field];
            if (value === undefined || value === null) {
              return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
            }
            return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{renderDefault(value)}</span>;
          }
          
          // CANONICAL PATTERN - match My Roster exactly (accrued only)
          const statsData = player.stats?.active_accrued?.pitching;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          const value = statsData[stat.field];
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          return <span className={`text-cyan-400 font-mono text-sm`}>{renderDefault(value)}</span>;
        }
      });
    });
  }
  // DD-STAT-CONSOLIDATION P0: removed duplicate static batter G/AB/H block (supplied via
  // the statConfigs prepend) and its `|| 0` / `?? 0` totals fallbacks (Rule 9).

  // Add dynamic stat columns
  statConfigs.forEach((config) => {
    // Extract simple field name for Team Totals table
    let columnKey = `stats.${config.field}`;
    if (isTeamTotalsTable) {
      let field = config.field;
      if (field.includes('.')) {
        field = field.split('.').pop(); // Get "runs" from "hitting.runs"
      }
      columnKey = field;
    }

    columns.push({
      key: columnKey,
      title: config.label,
      width: 32,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      isStatColumn: true,
      render: (valueOrPlayer, flatDataOrRowType, itemOrIndex) => {
        // Handle flat-data signature (for Team Totals table)
        if (isTeamTotalsTable && (typeof valueOrPlayer === 'number' || valueOrPlayer === null || valueOrPlayer === undefined)) {
          // Extract simple field name from config.field (e.g., "runs" from "hitting.runs")
          let fieldName = config.field;
          if (fieldName.includes('.')) {
            fieldName = fieldName.split('.').pop();
          }
          const value = valueOrPlayer ?? flatDataOrRowType?.[fieldName] ?? null;
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          let formattedValue;
          if (config.decimals === 3 && config.prefix === '.') {
            formattedValue = renderAvg(value);
          } else if (config.decimals === 3) {
            formattedValue = renderFloat3(value);
          } else if (config.decimals === 2) {
            formattedValue = renderFloat2(value);
          } else if (config.decimals === 1) {
            formattedValue = renderFloat1(value);
          } else {
            formattedValue = renderDefault(value);
          }
          return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{formattedValue}</span>;
        }
        // Handle regular player-data signature
        const player = valueOrPlayer;
        const rowType = flatDataOrRowType;
        const item = itemOrIndex;
        if (!player) {
          // Extract the actual field name (remove "hitting." or "pitching." prefix if present)
          let fieldName = config.field;
          if (fieldName.includes('.')) {
            const parts = fieldName.split('.');
            fieldName = parts[parts.length - 1];  // Get last part (e.g., "runs" from "hitting.runs")
          }
          
          const value = isTeamTotalsTable ? item.flatData?.[fieldName] : item.totals?.[fieldName];
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          let formattedValue;
          if (config.decimals === 3 && config.prefix === '.') {
            formattedValue = renderAvg(value);
          } else if (config.decimals === 3) {
            formattedValue = renderFloat3(value);
          } else if (config.decimals === 2) {
            formattedValue = renderFloat2(value);
          } else if (config.decimals === 1) {
            formattedValue = renderFloat1(value);
          } else {
            formattedValue = renderDefault(value);
          }
          
          return <span className={`${dynastyTheme.classes.text.white} font-bold text-sm font-mono`}>{formattedValue}</span>;
        }
        
        // CANONICAL PATTERN - match My Roster exactly (accrued only)
        const statsData = player.stats?.active_accrued;
        
        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        
        // Handle nested path like "pitching.era" or "hitting.avg"
        let value;
        if (config.field.includes('.')) {
          const parts = config.field.split('.');
          value = statsData;
          for (const part of parts) {
            if (!value) break;
            value = value[part];
          }
        } else {
          value = statsData[config.field];
        }
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        let formattedValue;
        if (config.decimals === 3 && config.prefix === '.') {
          formattedValue = renderAvg(value);
        } else if (config.decimals === 3) {
          formattedValue = renderFloat3(value);
        } else if (config.decimals === 2) {
          formattedValue = renderFloat2(value);
        } else if (config.decimals === 1) {
          formattedValue = renderFloat1(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        return <span className={`text-cyan-400 font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // PRICE, SALARY, CONTRACT columns - Skip for Team Totals table
  if (!isTeamTotalsTable) {
    columns.push({
      key: 'price',
      title: 'Price',
      width: 38,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: (player, rowType, item) => {
        if (!player) return null;
        
        const price = player.league_context?.financial?.baseline_price;
        return (
          <span className={`${dynastyTheme.classes.text.warning} font-medium text-sm`}>
            ${price}
          </span>
        );
      }
    });

    columns.push({
      key: 'salary',
      title: 'Salary',
      width: 42,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: (player, rowType, item) => {
        if (!player) return null;
        
        const salary = player.league_context?.financial?.contract_salary;
        return (
          <span className={`${dynastyTheme.classes.text.success} font-medium text-sm`}>
            ${salary}
          </span>
        );
      }
    });

    columns.push({
      key: 'contract',
      title: 'Contract',
      width: 52,  // Narrower
      cellPadding: 'py-1 px-1',
      sortable: false,
      className: () => 'bg-neutral-800',
      render: (player, rowType, item) => {
        if (!player) return null;

        const display = formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);

        return (
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : dynastyTheme.classes.text.neutralLight} text-sm`}>
            {display}
          </span>
        );
      }
    });
  }

  return columns;
};

// DD-STAT-CONSOLIDATION C6: calculateAccruedTotals RETIRED — the SSoT engine
// (services/tables/statTotals.computeTotals) is the single authority for totals.

/**
 * Separate current and historical players
 */
export const separateCurrentAndHistorical = (players) => {
  const current = players.filter(p => !p.info?.is_historical);
  const historical = players.filter(p => p.info?.is_historical);
  return { current, historical };
};
